/**
 * The customer config row as a form value: what staff edit, what a save
 * sends, and what is wrong with it. The Evals config and Eval PRs pages
 * each edit part of it, useEvalsConfigForm keeps the save, and the
 * components keep the markup.
 *
 * Every text field is a string so an empty one stays typable, and the
 * form is the same whether the team has a row or not: saving one creates
 * the row, which is how a customer is onboarded now. The three eval PR
 * fields are one unit, all set or all empty, like the columns they write.
 * The benchmark index file is optional and needs them.
 *
 * The row's `group_slug` and `group_display` are not typed here: the
 * night scores one of the benchmark rows, and a save sends that row's
 * eval group. The form holds which row (`scored_benchmark`), so renaming
 * the row's group keeps it scored. A row saved before its first benchmark
 * keeps the group it was saved with (`group`) until a benchmark is added.
 */

import type { BenchmarkAdapterItem, TeamEvalsConfig } from '@/api/generated/model'
import {
  entriesKey,
  entryFromConfig,
  entryToBody,
  escapesCheckout,
  newEntry,
  nonBlankLines,
  validateEntries,
  type EntryRow,
  type GroupRow,
} from './benchmarkSources'

export interface EvalsConfigForm {
  evals_enabled: boolean
  task_pipeline_enabled: boolean
  /**
   * The chain's GRPO training switch, preset and step count
   * (TrainingFields). Off whenever the task pipeline is: the run trains
   * what the gate proves.
   */
  training_enabled: boolean
  training_preset: string | null
  training_max_steps: number | null
  standing_deny_terms: string[]
  ack_empty_standing_terms: boolean
  /** One per line, as the text box holds them. */
  generic_terms: string
  tests_root: string
  tests_subdir: string
  registry_path: string
  review_base_url: string
  benchmark_sources: EntryRow[]
  /** The index of the benchmark the night scores, or null while none is picked. */
  scored_benchmark: number | null
  /**
   * The eval group as the row was saved, sent back while the row has no
   * benchmark. Nothing edits it: with a benchmark, the scored row's group
   * is the row's.
   */
  group: GroupRow
}

/** A team with no row yet: nothing filled, nothing armed. */
export function emptyForm(): EvalsConfigForm {
  return {
    evals_enabled: false,
    task_pipeline_enabled: false,
    training_enabled: false,
    training_preset: null,
    training_max_steps: null,
    standing_deny_terms: [],
    ack_empty_standing_terms: false,
    generic_terms: '',
    tests_root: '',
    tests_subdir: '',
    registry_path: '',
    review_base_url: '',
    benchmark_sources: [],
    scored_benchmark: null,
    group: { slug: '', display: '' },
  }
}

/**
 * Which row a save picks when the list changes: the only row, or the one
 * already picked. Null is a list of two or more with none picked.
 */
function scoredIndex(rows: readonly EntryRow[], picked: number | null): number | null {
  if (rows.length === 1) return 0
  return picked !== null && picked < rows.length ? picked : null
}

export function formFromConfig(config: TeamEvalsConfig): EvalsConfigForm {
  const rows = (config.benchmark_sources ?? []).map(entryFromConfig)
  const filling = rows.findIndex((row) => row.group.slug === config.group_slug)
  return {
    evals_enabled: config.evals_enabled,
    task_pipeline_enabled: config.task_pipeline_enabled,
    training_enabled: config.training_enabled,
    training_preset: config.training_preset,
    training_max_steps: config.training_max_steps,
    standing_deny_terms: [...(config.standing_deny_terms ?? [])],
    ack_empty_standing_terms: config.ack_empty_standing_terms ?? false,
    generic_terms: config.generic_terms.join('\n'),
    tests_root: config.tests_root ?? '',
    tests_subdir: config.tests_subdir ?? '',
    registry_path: config.registry_path ?? '',
    review_base_url: config.review_base_url ?? '',
    benchmark_sources: rows,
    scored_benchmark: scoredIndex(rows, filling === -1 ? null : filling),
    group: { slug: config.group_slug, display: config.group_display },
  }
}

/** The benchmark the night scores, or undefined while none is picked. */
export function scoredBenchmark(form: EvalsConfigForm): EntryRow | undefined {
  return form.scored_benchmark === null ? undefined : form.benchmark_sources[form.scored_benchmark]
}

/** A new blank row at the end. The first row is scored as soon as it exists. */
export function addBenchmark(form: EvalsConfigForm): EvalsConfigForm {
  const rows = [...form.benchmark_sources, newEntry()]
  return { ...form, benchmark_sources: rows, scored_benchmark: scoredIndex(rows, form.scored_benchmark) }
}

/** The list without row `index`. The picked row stays picked; removing it picks none. */
export function removeBenchmark(form: EvalsConfigForm, index: number): EvalsConfigForm {
  const rows = form.benchmark_sources.filter((_row, i) => i !== index)
  const picked = form.scored_benchmark
  const shifted = picked === null || picked === index ? null : picked > index ? picked - 1 : picked
  return { ...form, benchmark_sources: rows, scored_benchmark: scoredIndex(rows, shifted) }
}

/**
 * The eval PR fields, as the page labels them, in the order it shows
 * them. The benchmark index file is not one: it is optional, and needs them.
 */
export type LayoutField = 'tests_root' | 'tests_subdir' | 'review_base_url'

export const LAYOUT_FIELDS: readonly [LayoutField, string][] = [
  ['tests_root', 'tests directory'],
  ['tests_subdir', 'generated tests subdirectory'],
  ['review_base_url', 'review link base'],
]

/** The fields a save sends as null when empty: the layout and the index file. */
type NullableField = LayoutField | 'registry_path'

/** What the api says to an index file set on a row with no layout. */
export const INDEX_FILE_NEEDS_LAYOUT =
  'The benchmark index file needs the eval PR fields. Fill them in, or clear the index file.'

/** `a`, `a and b`, `a, b and c`: field names inside a sentence. */
export function spokenList(names: readonly string[]): string {
  const last = names[names.length - 1] ?? ''
  if (names.length <= 1) return last
  return `${names.slice(0, -1).join(', ')} and ${last}`
}

function layoutValue(form: EvalsConfigForm, field: NullableField): string {
  const value = form[field].trim()
  // Stripped exactly as the api stores it, or the saved row and the form
  // disagree by a slash and the form reads dirty forever after a save.
  return field === 'review_base_url' ? value.replace(/\/+$/, '') : value
}

/** Whether any eval PR field is filled: the layout is all or nothing. */
export function hasLayout(form: EvalsConfigForm): boolean {
  return LAYOUT_FIELDS.some(([field]) => layoutValue(form, field) !== '')
}

/**
 * The form as a save sends it. An empty eval PR field or index file is
 * null, never ''. The eval group is the scored benchmark's, or the saved
 * one while the row has no benchmark; validateForm refuses the other ways
 * of picking none before the save.
 */
export function toBody(form: EvalsConfigForm): TeamEvalsConfig {
  const layout = (field: NullableField): string | null => {
    const value = layoutValue(form, field)
    return value === '' ? null : value
  }
  const group = scoredBenchmark(form)?.group ?? form.group
  return {
    evals_enabled: form.evals_enabled,
    task_pipeline_enabled: form.task_pipeline_enabled,
    training_enabled: form.training_enabled,
    training_preset: form.training_preset,
    training_max_steps: form.training_max_steps,
    standing_deny_terms: trimTerms(form.standing_deny_terms),
    ack_empty_standing_terms: form.ack_empty_standing_terms,
    generic_terms: nonBlankLines(form.generic_terms),
    tests_root: layout('tests_root'),
    tests_subdir: layout('tests_subdir'),
    registry_path: layout('registry_path'),
    review_base_url: layout('review_base_url'),
    group_slug: group.slug.trim(),
    group_display: group.display.trim(),
    benchmark_sources: form.benchmark_sources.map(entryToBody),
  }
}

/** Deny terms as a save sends them: trimmed, blanks dropped. */
export function trimTerms(terms: readonly string[]): string[] {
  return terms.map((term) => term.trim()).filter((term) => term !== '')
}

/**
 * The first thing wrong with the deny list, or null. An empty list with
 * no acknowledgment saves: the Pipeline page says eval PRs wait on it.
 */
export function validateDenyList(form: EvalsConfigForm): string | null {
  const terms = trimTerms(form.standing_deny_terms)
  if (new Set(terms).size !== terms.length) return 'Each deny term is listed once.'
  return null
}

/**
 * The first generic term listed twice, as a sentence, or null. The
 * scanner compares them lower-cased, so `Pallet` and `pallet` are one
 * term. The list runs to a hundred lines, so the message names the term.
 */
export function validateGenericTerms(form: EvalsConfigForm): string | null {
  const seen = new Set<string>()
  for (const term of nonBlankLines(form.generic_terms)) {
    const key = term.toLowerCase()
    if (seen.has(key)) {
      return `"${term}" appears more than once in the generic terms. Letter case does not matter.`
    }
    seen.add(key)
  }
  return null
}

/** One canonical string per form, so what a save would drop is not a change. */
function formKey(form: EvalsConfigForm): string {
  const body = toBody(form)
  return JSON.stringify({ ...body, benchmark_sources: entriesKey(form.benchmark_sources) })
}

export function isFormDirty(saved: EvalsConfigForm, current: EvalsConfigForm): boolean {
  return formKey(saved) !== formKey(current)
}

/** The paths the jobs resolve inside the checkout, with what to call each. */
const CHECKOUT_PATHS: readonly [NullableField, string][] = [
  ['tests_root', 'The tests directory'],
  ['tests_subdir', 'The generated tests subdirectory'],
  ['registry_path', 'The benchmark index file'],
]

/**
 * The first thing wrong with the eval PR fields, or null. They come all
 * set or all empty, mirroring the api's `_refuse_partial_layout`, and a
 * set describes the repository of the scored benchmark, so that benchmark
 * has to be read from GitHub. The index file is optional, and needs them
 * (`_refuse_registry_without_layout`).
 */
export function validateLayout(form: EvalsConfigForm): string | null {
  if (!hasLayout(form)) {
    return layoutValue(form, 'registry_path') === '' ? null : INDEX_FILE_NEEDS_LAYOUT
  }
  const empty = LAYOUT_FIELDS.filter(([field]) => layoutValue(form, field) === '').map(
    ([, label]) => label,
  )
  if (empty.length > 0) {
    return `Fill in the ${spokenList(empty)} too, or clear every eval PR field.`
  }
  for (const [field, name] of CHECKOUT_PATHS) {
    if (escapesCheckout(form[field].trim())) {
      return `${name} is a path inside the repository, with no leading slash and no "..".`
    }
  }
  if (!/^https:\/\/[^/\s?#]+/.test(form.review_base_url.trim())) {
    return 'The review link base is an absolute https URL, e.g. https://acme.trainloop.ai'
  }
  const scored = scoredBenchmark(form)
  if (scored !== undefined && scored.source.kind !== 'github') {
    return 'The eval PR fields describe the repository of the scored benchmark, and that benchmark is read from S3. Score a GitHub benchmark, or clear every eval PR field.'
  }
  return null
}

/**
 * The first thing wrong with the scored benchmark, or null. The row's
 * eval group is a required column, and the night reads its tasks from the
 * group the scored benchmark fills. A row saved before its first benchmark
 * keeps its saved group and stays unarmed, as the api allows
 * (`_refuse_unscored_group`); a new row has no group to keep.
 */
export function validateScored(form: EvalsConfigForm): string | null {
  if (form.benchmark_sources.length === 0) {
    if (form.evals_enabled) {
      return 'Add the benchmark this team is scored on before turning scoring on.'
    }
    return form.group.slug === '' ? 'Add the benchmark the night scores. Its eval group is where the runs land.' : null
  }
  if (scoredBenchmark(form) === undefined) return 'Pick the benchmark the night scores.'
  return null
}

/**
 * The first thing wrong with the form, or null when it can be saved.
 * `adapters` is the shipped adapter list, which holds each one's routes.
 */
export function validateForm(
  form: EvalsConfigForm,
  adapters: readonly BenchmarkAdapterItem[],
): string | null {
  return (
    validateDenyList(form) ??
    validateGenericTerms(form) ??
    validateEntries(form.benchmark_sources, adapters) ??
    validateScored(form) ??
    validateLayout(form) ??
    validateTraining(form)
  )
}

/** Why the training settings cannot be saved, or null. The save refuses the same. */
export const TRAINING_NEEDS_SETTINGS =
  'Pick a training preset and a step count from 2 to 1000, or turn training off.'

/**
 * The api refuses training on without a preset and a step count within
 * the launch's bounds (internal.py `_refuse_training_without_settings`),
 * so the form says so before the save.
 */
export function validateTraining(form: EvalsConfigForm): string | null {
  if (!form.training_enabled) return null
  const steps = form.training_max_steps
  if (form.training_preset === null || steps === null || steps < 2 || steps > 1000) {
    return TRAINING_NEEDS_SETTINGS
  }
  return null
}
